<script setup lang="ts">
import { useOrderStore } from '../../../stores/orderForm';
import type { FileInfo } from '../../../types/File';
import type { Order } from '../../../types/Order';
import { formatDate } from '../../../utils/date';
import { formatFileSize } from '../../../utils/files';

type Props = {
  pendingFile?: File
  uploadedFile?: FileInfo
  uploadMeta?: { progress: number, uploading: boolean, uploaded: boolean }
  order?: Order
};
const props = defineProps<Props>();
const emit = defineEmits<{ (e: 'remove'): void }>();
const { pendingFile, uploadedFile, uploadMeta, order } = toRefs(props);

const { isPreviewMode } = useOrderStore();

const severity = computed(() => {
  if (uploadedFile?.value?.status === 'deleted') {
    return 'error';
  }
  if (uploadMeta?.value?.uploaded || isPreviewMode) {
    return 'success';
  }

  return 'info';
});
</script>

<template>
  <Message
    :severity="severity"
    class="relative m-0"
    :pt="{
      wrapper: 'p-3 text-base',
      closeButton: 'w-auto h-auto rounded-none',
      text: 'w-1/2 flex-grow-1',
    }"
    @close="emit('remove')"
  >
    <template #messageicon>
      <i v-if="uploadMeta?.uploading" class="pi pi pi-spin pi-spinner" />
      <i v-else class="pi pi-file" />
    </template>
    <template #closeicon>
      <span v-if="isPreviewMode || uploadMeta?.uploading || uploadMeta?.uploaded" />
      <i v-else v-tooltip.top="'Премахни'" class="pi pi-times-circle cursor-pointer" />
    </template>

    <div v-if="pendingFile" class="w-full px-3">
      <p v-tooltip.top="pendingFile.name" class="truncate font-semibold">
        {{ pendingFile.name }}
      </p>
      <p>{{ formatFileSize(pendingFile.size) }} {{ formatDate(new Date(pendingFile.lastModified), 'datetime') }}</p>
    </div>

    <div v-if="uploadedFile" class="w-full px-3">
      <FileDownloaderLink v-if="uploadedFile.id" :info="uploadedFile" :order="order" />
    </div>
    <ProgressBar
      v-if="uploadMeta?.uploading"
      class="absolute bottom-0 w-[calc(100%+0.25rem)] -left-1"
      :value="uploadMeta?.progress"
      :show-value="false"
      :pt="{ root: 'h-[6px]', value: 'bg-[#3b82f6]' }"
    />
  </Message>
</template>

<style scoped>

</style>
